import { useQuery } from '@apollo/client/react';
import type { ReactNode } from 'react';
import { use, useState } from 'react';
import ListSubheader from '@mui/material/ListSubheader';
import CircularProgress from '@mui/material/CircularProgress';
import { tss } from 'tss-react/mui';

import { graphql, type DocumentType } from '@/gql';

import { nonFalsey } from 'Utils/nonFalsey';
import { extractPaginationProps } from '@/utils/queries/queryExtracts';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import { POST_LIMIT } from '@/modules/group/noticeboard/PostCollection';

import type { MULTI_GROUPS_POST_FRAGMENT } from './MultiGroupPostCollection';
import { MultiGroupPostCollection } from './MultiGroupPostCollection';
import { MultiGroupPinnedPostsAccordion } from './MultiGroupPinnedPostsAccordion';

interface Props {
  groupIds: string[] | null;
}

export const USERS_GROUPS_POSTS_QUERY = graphql(`
  query UsersGroupsPostsQuery(
    $userId: ID!
    $first: Int
    $after: String
    $groupIds: [ID]
  ) {
    user(id: $userId) {
      _id
      pinnedDashboardPostsConnection: groupPostsConnection(
        first: null
        after: ""
        filterBy: { pinned: true, groupIds: $groupIds }
        orderBy: LASTMODIFIED_DESC
      ) @connection(key: "dashboardpinnedposts") {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...multiGroupsPostFragment
          }
        }
      }
      groupPostsConnection(
        first: $first
        after: $after
        filterBy: { groupIds: $groupIds }
        orderBy: CREATED_DESC
      ) @connection(key: "dashboardposts") {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...multiGroupsPostFragment
          }
        }
      }
    }
  }
`);

const USERS_GROUPS_POSTS_PAGE_QUERY = graphql(`
  query UsersGroupsPostsPage(
    $userId: ID!
    $first: Int
    $after: String
    $groupIds: [ID]
  ) {
    user(id: $userId) {
      _id
      groupPostsConnection(
        first: $first
        after: $after
        filterBy: { groupIds: $groupIds }
        orderBy: CREATED_DESC
      ) @connection(key: "dashboardposts") {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...multiGroupsPostFragment
          }
        }
      }
    }
  }
`);

const useStyles = tss.create(({ theme }) => {
  return {
    listSubheaderBar: { display: 'flex', alignItems: 'center' },
    listSubheaderLoading: { marginLeft: 'auto', marginRight: theme.spacing(2) },
  };
});

interface PostCollectionHeaderProps {
  loading: boolean;
  title: ReactNode;
  collectionLength: number;
}

export const PostCollectionHeader = ({
  loading,
  title,
  collectionLength,
}: PostCollectionHeaderProps) => {
  const { classes } = useStyles();

  return collectionLength || loading ? (
    <div className={classes.listSubheaderBar}>
      <ListSubheader disableSticky>{title}</ListSubheader>
      {loading ? (
        <CircularProgress
          className={classes.listSubheaderLoading}
          disableShrink
          size={16}
        />
      ) : null}
    </div>
  ) : null;
};

export const MultiGroupPostsEntry = ({ groupIds }: Props) => {
  const { currentUserId } = use(CurrentUserContext);

  const [fetchOutstanding, setFetchOutstanding] = useState(false);

  const { loading, data, fetchMore } = useQuery(USERS_GROUPS_POSTS_QUERY, {
    variables: {
      userId: currentUserId || '',
      first: POST_LIMIT,
      after: null,
      groupIds,
    },
    skip: !currentUserId,
    fetchPolicy: 'cache-and-network',
  });

  const user = data?.user;

  const {
    nodes: posts,
    endCursor,
    hasNextPage: hasMorePosts,
  } = extractPaginationProps(user?.groupPostsConnection);

  const pinnedPosts: Array<DocumentType<typeof MULTI_GROUPS_POST_FRAGMENT>> =
    user?.pinnedDashboardPostsConnection?.edges
      .map((edge) => {
        return edge?.node ?? null;
      })
      .filter(nonFalsey) || [];

  return (
    <>
      <MultiGroupPinnedPostsAccordion loading={loading} posts={pinnedPosts} />
      <PostCollectionHeader
        loading={loading}
        title={'New posts'}
        collectionLength={posts.length}
      />
      <MultiGroupPostCollection
        hasMorePosts={hasMorePosts}
        loadMorePosts={async () => {
          if (fetchOutstanding) {
            return;
          }
          setFetchOutstanding(true);
          await fetchMore({
            query: USERS_GROUPS_POSTS_PAGE_QUERY,
            variables: {
              userId: currentUserId || '',
              first: POST_LIMIT,
              after: endCursor,
              groupIds,
            },
          });
          setFetchOutstanding(false);
        }}
        posts={posts}
        loading={loading}
      />
    </>
  );
};
